import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { HiMiniBars3 } from 'react-icons/hi2';
import { FaShoppingCart, FaChevronDown } from 'react-icons/fa';
import { MdOutlineAccountCircle } from 'react-icons/md';
import Popper from '@mui/material/Popper';
import { useTranslation } from 'react-i18next';
import globeIcon from "./Assets/globe.png";
import { fetchLanguages } from '../i18n';
import { CURRENCIES, CURRENCY_SELECTION_ENABLED, useCurrency } from '../utils/currency';
// import logo from './Assets/Xianhe New Assets/logo-topbar.png';
import logo from './Assets/iconHeader.png';

// Adjustable, responsive nudge for the desktop nav.
const NAV_OFFSET = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_NAV_OFFSET)
  ? process.env.REACT_APP_NAV_OFFSET
  : 'clamp(8px, 2vw, 24px)';

function TopBar({
  variant = 'default',
  cartCount = 0,
  isLoggedIn = false,
  onCartClick,
  onMySpaceClick,
  onSignInClick,
}) {
  const MOBILE_BREAKPOINT = 960;
  const COMPACT_DESKTOP_MAX_WIDTH = 1500;
  const COMPACT_DESKTOP_MAX_HEIGHT = 900;

  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BREAKPOINT : false
  );
  const [isCompactDesktop, setIsCompactDesktop] = useState(
    typeof window !== 'undefined'
      ? window.innerWidth > MOBILE_BREAKPOINT &&
          (window.innerWidth <= COMPACT_DESKTOP_MAX_WIDTH || window.innerHeight <= COMPACT_DESKTOP_MAX_HEIGHT)
      : false
  );
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const [activeMenu, setActiveMenu] = useState(null); // 'language', 'destiny', 'appointments'
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const timerRef = useRef(null);
  const [mobileLangOpen, setMobileLangOpen] = useState(false);
  const [mobileCurrencyOpen, setMobileCurrencyOpen] = useState(false);
  const { currency, setCurrency } = useCurrency();

  const normalizeLocale = (code) => {
    const c = (code || '').split(/[-_]/)[0].toLowerCase();
    if (c.startsWith('zh')) return 'cn';
    return c || 'en';
  };

  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    const fromI18n = normalizeLocale(i18n.language);
    const fromStorage = localStorage.getItem('language') || 'en';
    return fromI18n || fromStorage;
  });
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [dropdownTop, setDropdownTop] = useState(0);

  // Clear timeouts on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Fetch available languages from API
  useEffect(() => {
    const fallbackLanguages = [
      { Id: '2224', LookupDetail: 'en', LookupDesc: 'English' },
      { Id: '2225', LookupDetail: 'id', LookupDesc: 'Bahasa Indonesia' },
      { Id: '2226', LookupDetail: 'cn', LookupDesc: '中文' },
    ];

    fetchLanguages().then(languages => {
      if (!languages || languages.length === 0) {
        setAvailableLanguages(fallbackLanguages);
        return;
      }

      const mergedLangs = [...languages];
      const existingCodes = mergedLangs.map(lang => {
        let code = (lang.LookupDetail || '').match(/^(\S+)/)?.[1]?.toLowerCase() || '';
        if (code.startsWith('zh')) code = 'cn';
        return code;
      });

      fallbackLanguages.forEach(fallback => {
        let fallbackCode = (fallback.LookupDetail || '').match(/^(\S+)/)?.[1]?.toLowerCase() || '';
        if (fallbackCode.startsWith('zh')) fallbackCode = 'cn';
        if (!existingCodes.includes(fallbackCode)) {
          mergedLangs.push(fallback);
        }
      });

      setAvailableLanguages(mergedLangs);
    }).catch((err) => {
      setAvailableLanguages(fallbackLanguages);
    });
  }, []);

  // Reports whether the server accepted the change. Callers must await this before reloading:
  // `syncProfileLanguageFromGetProfile` re-reads the profile on load and overwrites the local
  // language keys, so a reload that beats this POST silently reverts the user's choice.
  const updateUserLanguage = async (langId, langDesc) => {
    try {
      const userId = localStorage.getItem('userID');
      if (!userId) return true; // Signed out — nothing to persist server-side.
      const response = await fetch('https://xianhefairy.com/LotusFairy_API/api/User/LanguageUpdate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ID: parseInt(userId), LANG_ID: langId, LANG_DESC: langDesc })
      });
      if (!response.ok) console.error('Language update rejected:', response.status);
      return response.ok;
    } catch (error) {
      console.error('Error updating user language:', error);
      return false;
    }
  };

  const handleOpen = (e, name) => {
    if (isMobile) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setMenuAnchorEl(e.currentTarget);
    setActiveMenu(name);
  };

  const handleClose = () => {
    if (isMobile) return;
    timerRef.current = setTimeout(() => {
      setActiveMenu(null);
      setMenuAnchorEl(null);
    }, 500);
  };

  const keepOpen = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  useEffect(() => {
    const onResize = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const mobile = viewportWidth <= MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      setIsCompactDesktop(
        !mobile &&
          (viewportWidth <= COMPACT_DESKTOP_MAX_WIDTH || viewportHeight <= COMPACT_DESKTOP_MAX_HEIGHT)
      );
      if (!mobile) setOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [COMPACT_DESKTOP_MAX_HEIGHT, COMPACT_DESKTOP_MAX_WIDTH, MOBILE_BREAKPOINT]);

  const desktopNavGap = isCompactDesktop ? 'clamp(16px, 1.9vw, 30px)' : styles.navLinks.gap;
  const desktopLinkStyle = isCompactDesktop ? { fontSize: '13px' } : null;
  const desktopActionButtonStyle = isCompactDesktop
    ? { padding: '5px 11px', fontSize: 12, gap: 5 }
    : null;

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (!isMobile) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          buttonRef.current && !buttonRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    document.body.style.overflow = open ? 'hidden' : 'auto';
  }, [open, isMobile]);

  useEffect(() => {
    if (!(isMobile && open)) return;
    const updateTop = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownTop(Math.round(rect.bottom));
      }
    };
    updateTop();
    window.addEventListener('resize', updateTop);
    window.addEventListener('scroll', updateTop, true);
    return () => {
      window.removeEventListener('resize', updateTop);
      window.removeEventListener('scroll', updateTop, true);
    };
  }, [isMobile, open]);

  // Close mobile language dropdown on outside click
  useEffect(() => {
    if (!mobileLangOpen) return;
    const handler = (e) => {
      if (!e.target.closest('[data-lang-trigger]') && !e.target.closest('[data-lang-menu]')) {
        setMobileLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [mobileLangOpen]);

  // Close mobile currency dropdown on outside click
  useEffect(() => {
    if (!mobileCurrencyOpen) return;
    const handler = (e) => {
      if (!e.target.closest('[data-currency-trigger]') && !e.target.closest('[data-currency-menu]')) {
        setMobileCurrencyOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [mobileCurrencyOpen]);

  const isActive = (path) => {
    if (path === '/main') return ['/main', '/natal', '/DigitalCharm'].includes(location.pathname);
    if (path === '/pricing') return ['/pricing', '/affiliatepartner'].includes(location.pathname);
    if (path === '/chart') return ['/chart', '/DigitalCharm'].includes(location.pathname);
    if (path === '/myappointment') return ['/myappointment', '/schedule'].includes(location.pathname);
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleCartClick = () => { if (onCartClick) onCartClick(); else navigate('/cart'); };
  const handleMySpaceClick = () => { if (onMySpaceClick) onMySpaceClick(); else navigate('/account'); };
  const handleSignIn = () => { if (onSignInClick) onSignInClick(); };

  // Signed-in users change language from Account > Language Settings, which persists the choice
  // to their profile. The topbar switcher stays for signed-out visitors, who have no profile to
  // save to; for signed-in users it becomes a read-only indicator (no chevron, no dropdown).
  const canChangeLang = !isLoggedIn;

  // Derive short language label (e.g. "ENG")
  const getLangLabel = () => {
    const match = availableLanguages.find(l => {
      let code = (l.LookupDetail || '').match(/^(\S+)/)?.[1]?.toLowerCase() || '';
      if (code.startsWith('zh')) code = 'cn';
      return code === selectedLanguage;
    });
    if (!match) return selectedLanguage.toUpperCase();
    return match.LookupDetail.split(' ')[0].substring(0, 3).toUpperCase();
  };

  const renderLangItems = (onAfterSelect) =>
    availableLanguages.map((lang) => {
      let langCode = (lang.LookupDetail || '').match(/^(\S+)/)?.[1]?.toLowerCase() || '';
      if (langCode.startsWith('zh')) langCode = 'cn';
      const isSelected = selectedLanguage === langCode;
      return (
        <div
          key={lang.Id}
          style={{
            padding: '8px 12px',
            cursor: 'pointer',
            backgroundColor: isSelected ? 'rgba(223,189,105,0.2)' : 'transparent',
            borderRadius: 4,
            color: '#DFBD69',
            fontSize: 14,
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
          onClick={async () => {
            setSelectedLanguage(langCode);
            localStorage.setItem('language', langCode);
            localStorage.setItem('languageId', lang.Id);
            // LanguageSettings seeds its ticked row from `profileLanguage`, so skipping it here
            // leaves that page showing the previous language while the app renders the new one.
            localStorage.setItem('profileLanguage', langCode);
            sessionStorage.setItem('topBarLangOverride', langCode);
            await i18n.changeLanguage(langCode);
            setActiveMenu(null);
            setMobileLangOpen(false);
            if (onAfterSelect) onAfterSelect();
            // Await the save before reloading — the old fire-and-forget call raced a 300ms
            // timer, and losing that race meant the profile sync restored the previous language.
            await updateUserLanguage(lang.Id, lang.LookupDesc);
            window.location.reload();
          }}
        >
          {isSelected && '✓ '}{lang.LookupDesc}
        </div>
      );
    });

  const langDropdownBox = (onAfterSelect) => (
    <div style={{
      background: 'black',
      minWidth: 160,
      boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
      borderRadius: 4,
      padding: 8,
      border: '1px solid #DFBD69',
    }}>
      {renderLangItems(onAfterSelect)}
    </div>
  );

  const currencyDropdownBox = () => (
    <div style={{ ...styles.popperBox, minWidth: 200 }}>
      {Object.values(CURRENCIES).map((c) => {
        const isSelected = currency === c.code;
        return (
          <div
            key={c.code}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              backgroundColor: isSelected ? 'rgba(223,189,105,0.2)' : 'transparent',
              borderRadius: 4,
              color: '#DFBD69',
              fontSize: 14,
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
            onClick={() => {
              setCurrency(c.code);
              setActiveMenu(null);
              setMobileCurrencyOpen(false);
            }}
          >
            {isSelected && '✓ '}{c.label}
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      style={{
        ...styles.topBar,
        ...(variant === 'embedded' ? styles.embeddedTopBar : {}),
        padding: isCompactDesktop ? '8px 12px' : styles.topBar.padding,
      }}
    >
      <style>{`
        .nav-link {
          text-decoration: none;
          color: #FFFFFF;
          font-family: 'PlayfairDisplay', serif;
          font-weight: 600;
          font-size: 14px;
          background: transparent;
          border: none;
          cursor: pointer;
          transition: color .25s;
          user-select: none;
        }
        .nav-link:hover { color: #DFBD69; text-shadow: 0 0 4px rgba(223,189,105,0.35); }
        .action-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          border: 1px solid rgba(255,255,255,0.35);
          border-radius: 20px;
          background: transparent;
          color: #fff;
          font-family: 'DMSans', sans-serif;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: border-color .2s, color .2s;
          white-space: nowrap;
          position: relative;
        }
        .action-btn:hover { border-color: #DFBD69; color: #DFBD69; }
      `}</style>

      {isMobile ? (
        /* ── MOBILE LAYOUT ── */
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '0 4px' }}>
            {/* Hamburger */}
            <button ref={buttonRef} style={styles.menuButton} onClick={() => setOpen(o => !o)}>
              <HiMiniBars3 />
            </button>

            {/* Centered logo */}
            <Link to="/main" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center' }}>
              <img src={logo} alt="Logo" style={{ width: "70px", height: "70px", objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
            </Link>

            {/* Right: cart badge + language */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isLoggedIn && (
                <button className="action-btn" style={{ padding: '5px 10px' }} onClick={handleCartClick} aria-label="Cart">
                  <FaShoppingCart size={15} />
                  {cartCount > 0 && (
                    <span style={{ position: 'absolute', top: -5, right: -5, background: '#deaf77', color: '#fff', borderRadius: '50%', width: 15, height: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 'bold', border: '1.5px solid #000' }}>
                      {cartCount > 99 ? '99+' : cartCount}
                    </span>
                  )}
                </button>
              )}
              {/* Mobile currency — prices across the shop follow this choice */}
              {CURRENCY_SELECTION_ENABLED && (<div style={{ position: 'relative' }} data-currency-trigger="true">
                <button
                  type="button"
                  className="action-btn"
                  style={{ padding: '5px 10px', gap: 4 }}
                  onClick={() => setMobileCurrencyOpen(o => !o)}
                  aria-label="Currency"
                >
                  <span style={{ fontSize: 11 }}>{currency}</span>
                  <FaChevronDown size={9} />
                </button>
                {mobileCurrencyOpen && (
                  <div data-currency-menu="true" style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 15000 }}>
                    {currencyDropdownBox()}
                  </div>
                )}
              </div>)}
              {/* Mobile language — only shown before sign-in; signed-in users
                  change language from Account > Language Settings instead. */}
              {!isLoggedIn && (
                <div style={{ position: 'relative' }} data-lang-trigger="true">
                  <button
                    type="button"
                    className="action-btn"
                    aria-disabled={!canChangeLang}
                    style={{ padding: '5px 10px', gap: 4, cursor: canChangeLang ? 'pointer' : 'default' }}
                    onClick={() => canChangeLang && setMobileLangOpen(o => !o)}
                  >
                    <img src={globeIcon} alt="Language" style={{ width: 15, height: 15 }} />
                    <span style={{ fontSize: 11 }}>{getLangLabel()}</span>
                    {canChangeLang && <FaChevronDown size={9} />}
                  </button>
                  {canChangeLang && mobileLangOpen && (
                    <div data-lang-menu="true" style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 15000 }}>
                      {langDropdownBox()}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile slide-down nav */}
          <div style={{
            ...styles.mobileDropdownContainer,
            top: dropdownTop,
            maxHeight: open ? 420 : 0,
            opacity: open ? 1 : 0,
            pointerEvents: open ? 'auto' : 'none',
            transition: 'max-height .35s cubic-bezier(.4,0,.2,1), opacity .25s',
          }}>
            <ul ref={dropdownRef} style={styles.mobileNavLinks}>
              <li>
                <button style={{ ...styles.link, ...(isActive('/main') ? styles.activeLink : {}) }} onClick={() => setActiveMenu(activeMenu === 'lifeinform' ? null : 'lifeinform')}>
                  {t('mainPage.home')}
                </button>
                {activeMenu === 'lifeinform' && (
                  <div style={{ background: '#111', width: '100%', textAlign: 'center', padding: '10px 0' }}>
                    <div className="nav-link" style={{ padding: '6px 0' }} onClick={() => { navigate('/main'); setOpen(false); setActiveMenu(null); }}>{t('mainPage.landing')}</div>
                    <div className="nav-link" style={{ padding: '6px 0' }} onClick={() => { navigate('/natal'); setOpen(false); setActiveMenu(null); }}>{t('natalChart.yourLifeChart')}</div>
                    <div className="nav-link" style={{ padding: '6px 0' }} onClick={() => { navigate('/DigitalCharm'); setOpen(false); setActiveMenu(null); }}>{t('mainPage.myAmulet')}</div>
                  </div>
                )}
              </li>
              <li><Link to="/courses" style={{ ...styles.link, ...(isActive('/courses') ? styles.activeLink : {}) }} onClick={() => setOpen(false)}>{t('mainPage.courses')}</Link></li>
              <li>
                <button style={{ ...styles.link, ...(isActive('/myappointment') ? styles.activeLink : {}) }} onClick={() => setActiveMenu(activeMenu === 'appointments' ? null : 'appointments')}>
                  {t('mainPage.appointments')}
                </button>
                {activeMenu === 'appointments' && (
                  <div style={{ background: '#111', width: '100%', textAlign: 'center', padding: '10px 0' }}>
                    <div className="nav-link" style={{ padding: '6px 0' }} onClick={() => { navigate('/schedule'); setOpen(false); }}>{t('mainPage.makeAppointment')}</div>
                    <div className="nav-link" style={{ padding: '6px 0' }} onClick={() => { navigate('/myappointment'); setOpen(false); }}>{t('mainPage.checkAppointment')}</div>
                  </div>
                )}
              </li>
              <li><Link to="/shop" style={{ ...styles.link, ...(isActive('/shop') ? styles.activeLink : {}) }} onClick={() => setOpen(false)}>{t('mainPage.shop')}</Link></li>
              <li><Link to="/balanceinspace" style={{ ...styles.link, ...(isActive('/balanceinspace') ? styles.activeLink : {}) }} onClick={() => setOpen(false)}>{t('mainPage.balanceInSpace')}</Link></li>
              <li>
                <button style={{ ...styles.link, ...(isActive('/pricing') ? styles.activeLink : {}) }} onClick={() => setActiveMenu(activeMenu === 'membership' ? null : 'membership')}>
                  {t('mainPage.subscribePlan')}
                </button>
                {activeMenu === 'membership' && (
                  <div style={{ background: '#111', width: '100%', textAlign: 'center', padding: '10px 0' }}>
                    <div className="nav-link" style={{ padding: '6px 0' }} onClick={() => { navigate('/pricing'); setOpen(false); setActiveMenu(null); }}>{t('mainPage.plan')}</div>
                    <div className="nav-link" style={{ padding: '6px 0' }} onClick={() => { navigate('/affiliatepartner'); setOpen(false); setActiveMenu(null); }}>{t('mainPage.affiliatePartner')}</div>
                  </div>
                )}
              </li>
              {isLoggedIn ? (
                <li>
                  <button className="action-btn" data-myspace-trigger="true" onClick={() => { handleMySpaceClick(); setOpen(false); }}>
                    <MdOutlineAccountCircle size={15} />
                    My Space
                  </button>
                </li>
              ) : (
                <li>
                  <button className="action-btn" onClick={() => { handleSignIn(); setOpen(false); }}>Sign In</button>
                </li>
              )}
            </ul>
          </div>
        </>
      ) : (
        /* ── DESKTOP LAYOUT ── */
        <div style={{ display: 'flex', alignItems: 'center', width: '100%', padding: isCompactDesktop ? '2px 0' : '4px 0' }}>
          {/* Left nav */}
          <ul
            style={{
              ...styles.navLinks,
              gap: desktopNavGap,
              flex: 1,
              justifyContent: 'flex-end',
              paddingRight: isCompactDesktop ? 'clamp(10px, 1.8vw, 24px)' : 'clamp(16px, 3vw, 48px)',
            }}
          >
            <li style={{ position: 'relative' }} onMouseEnter={(e) => handleOpen(e, 'lifeinform')} onMouseLeave={handleClose}>
              <button style={{ ...styles.link, ...desktopLinkStyle, ...(isActive('/main') ? styles.activeLink : {}) }}>
                {t('mainPage.home')}
              </button>
              <Popper open={activeMenu === 'lifeinform'} anchorEl={menuAnchorEl} placement="bottom" style={{ zIndex: 13000 }}>
                <div onMouseEnter={keepOpen} onMouseLeave={handleClose} style={{ paddingTop: 10 }}>
                  <div style={styles.popperBox}>
                    <div className="nav-link" style={styles.popperItem} onClick={() => { navigate('/main'); setActiveMenu(null); }}>{t('mainPage.landing')}</div>
                    <div className="nav-link" style={styles.popperItem} onClick={() => { navigate('/natal'); setActiveMenu(null); }}>{t('natalChart.yourLifeChart')}</div>
                    <div className="nav-link" style={styles.popperItem} onClick={() => { navigate('/DigitalCharm'); setActiveMenu(null); }}>{t('mainPage.myAmulet')}</div>
                  </div>
                </div>
              </Popper>
            </li>
            <li>
              <Link to="/courses" style={{ ...styles.link, ...desktopLinkStyle, ...(isActive('/courses') ? styles.activeLink : {}) }}>{t('mainPage.courses')}</Link>
            </li>
            <li style={{ position: 'relative' }} onMouseEnter={(e) => handleOpen(e, 'appointments')} onMouseLeave={handleClose}>
              <button style={{ ...styles.link, ...desktopLinkStyle, ...(isActive('/myappointment') ? styles.activeLink : {}) }}>
                {t('mainPage.appointments')}
              </button>
              <Popper open={activeMenu === 'appointments'} anchorEl={menuAnchorEl} placement="bottom" style={{ zIndex: 13000 }}>
                <div onMouseEnter={keepOpen} onMouseLeave={handleClose} style={{ paddingTop: 10 }}>
                  <div style={styles.popperBox}>
                    <div className="nav-link" style={styles.popperItem} onClick={() => { navigate('/schedule'); setActiveMenu(null); }}>{t('mainPage.makeAppointment')}</div>
                    <div className="nav-link" style={styles.popperItem} onClick={() => { navigate('/myappointment'); setActiveMenu(null); }}>{t('mainPage.checkAppointment')}</div>
                  </div>
                </div>
              </Popper>
            </li>
            <li>
              <Link to="/shop" style={{ ...styles.link, ...desktopLinkStyle, ...(isActive('/shop') ? styles.activeLink : {}) }}>{t('mainPage.shop')}</Link>
            </li>
          </ul>

          {/* Center logo */}
          <Link
            to="/main"
            style={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              padding: isCompactDesktop ? '0 clamp(8px, 1.6vw, 20px)' : '0 clamp(14px, 2.5vw, 40px)',
            }}
          >
            <img
              src={logo}
              alt="Logo"
              style={{
                height: isCompactDesktop ? 44 : 50,
                objectFit: 'contain',
                filter: 'brightness(0) invert(1)',
              }}
            />
          </Link>

          {/* Right: Membership + actions */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              paddingLeft: isCompactDesktop ? 'clamp(10px, 1.8vw, 24px)' : 'clamp(16px, 3vw, 48px)',
              gap: isCompactDesktop ? 10 : 16,
            }}
          >
            <ul style={{ ...styles.navLinks, gap: desktopNavGap, flex: 'none' }}>
              <li>
                <Link to="/balanceinspace" style={{ ...styles.link, ...desktopLinkStyle, ...(isActive('/balanceinspace') ? styles.activeLink : {}) }}>{t('mainPage.balanceInSpace')}</Link>
              </li>
              <li style={{ position: 'relative' }} onMouseEnter={(e) => handleOpen(e, 'membership')} onMouseLeave={handleClose}>
                <button style={{ ...styles.link, ...desktopLinkStyle, ...(isActive('/pricing') ? styles.activeLink : {}) }}>
                  {t('mainPage.subscribePlan')}
                </button>
                <Popper open={activeMenu === 'membership'} anchorEl={menuAnchorEl} placement="bottom" style={{ zIndex: 13000 }}>
                  <div onMouseEnter={keepOpen} onMouseLeave={handleClose} style={{ paddingTop: 10 }}>
                    <div style={styles.popperBox}>
                      <div className="nav-link" style={styles.popperItem} onClick={() => { navigate('/pricing'); setActiveMenu(null); }}>{t('mainPage.plan')}</div>
                      <div className="nav-link" style={styles.popperItem} onClick={() => { navigate('/affiliatepartner'); setActiveMenu(null); }}>{t('mainPage.affiliatePartner')}</div>
                    </div>
                  </div>
                </Popper>
              </li>
            </ul>

            <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              {/* Currency selector — sits in the gap between the nav and the action buttons */}
              {/* <div style={{ position: 'relative' }}
                onMouseEnter={(e) => handleOpen(e, 'currency')}
                onMouseLeave={handleClose}
              >
                <button
                  type="button"
                  className="action-btn"
                  style={{ ...(desktopActionButtonStyle || {}), gap: 5 }}
                  aria-label="Currency"
                >
                  <span>{CURRENCIES[currency].symbol} {currency}</span>
                  <FaChevronDown size={10} />
                </button>
                <Popper open={activeMenu === 'currency'} anchorEl={menuAnchorEl} placement="bottom" style={{ zIndex: 13000 }}>
                  <div onMouseEnter={keepOpen} onMouseLeave={handleClose} style={{ paddingTop: 6 }}>
                    {currencyDropdownBox()}
                  </div>
                </Popper>
              </div> */}
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isLoggedIn && (
                <button className="action-btn" style={desktopActionButtonStyle || undefined} onClick={handleCartClick}>
                  <FaShoppingCart size={14} />
                  {t('cart.yourCart')}
                  {cartCount > 0 && (
                    <span style={{ position: 'absolute', top: -6, right: -6, background: '#deaf77', color: '#fff', borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 'bold', border: '1.5px solid #000' }}>
                      {cartCount > 99 ? '99+' : cartCount}
                    </span>
                  )}
                </button>
              )}
              {isLoggedIn ? (
                <button className="action-btn" style={desktopActionButtonStyle || undefined} data-myspace-trigger="true" onClick={handleMySpaceClick}>
                  <MdOutlineAccountCircle size={16} />
                  {t('tryoutpage.myspace')}
                </button>
              ) : (
                <button className="action-btn" style={desktopActionButtonStyle || undefined} onClick={handleSignIn}>Sign In</button>
              )}
              {/* Language selector — only shown before sign-in; signed-in
                  users change language from Account > Language Settings instead. */}
              {!isLoggedIn && (
                <div style={{ position: 'relative' }}
                  onMouseEnter={(e) => canChangeLang && handleOpen(e, 'language')}
                  onMouseLeave={handleClose}
                >
                  <button
                    type="button"
                    className="action-btn"
                    aria-disabled={!canChangeLang}
                    style={{ ...(desktopActionButtonStyle || {}), gap: 5, cursor: canChangeLang ? 'pointer' : 'default' }}
                  >
                    <img src={globeIcon} alt="Language" style={{ width: 15, height: 15 }} />
                    <span>{getLangLabel()}</span>
                    {canChangeLang && <FaChevronDown size={10} />}
                  </button>
                  {canChangeLang && (
                    <Popper open={activeMenu === 'language'} anchorEl={menuAnchorEl} placement="bottom-end" style={{ zIndex: 13000 }}>
                      <div onMouseEnter={keepOpen} onMouseLeave={handleClose} style={{ paddingTop: 6 }}>
                        {langDropdownBox()}
                      </div>
                    </Popper>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  topBar: { width: '100%', backgroundColor: 'transparent', padding: '8px 20px', position: 'relative', zIndex: 21000, isolation: 'isolate' },
  embeddedTopBar: { width: '100%', padding: '8px 20px', backgroundColor: 'transparent' },
  menuButton: { background: 'transparent', border: 'none', padding: 8, color: '#FFFFFF', fontSize: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  mobileDropdownContainer: { position: 'fixed', left: 0, right: 0, background: 'black', boxShadow: '0 6px 18px rgba(0,0,0,0.45)', width: '100%', overflow: 'hidden', borderRadius: '0 0 10px 10px', zIndex: 22000 },
  mobileNavLinks: { listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 18, margin: 0, padding: '16px 0', alignItems: 'center', justifyContent: 'center' },
  navLinks: { listStyle: 'none', display: 'flex', gap: 'clamp(36px, 3.8vw, 64px)', margin: 0, padding: 0 },
  link: { textDecoration: 'none', color: '#FFFFFF', fontFamily: "'PlayfairDisplay', serif", fontWeight: 400, fontSize: "14px", background: 'transparent', border: 'none', cursor: 'pointer', transition: 'color .25s', userSelect: 'none' },
  activeLink: { color: '#DFBD69', textShadow: '0 0 4px rgba(223,189,105,0.35)', borderBottom: '2px solid #DFBD69', paddingBottom: 2 },
  popperBox: { background: 'black', color: '#DFBD69', minWidth: 160, boxShadow: '0 2px 8px rgba(0,0,0,0.2)', borderRadius: 4, padding: 8, border: '1px solid #DFBD69' },
  popperItem: { padding: '8px 12px', display: 'block' },
};

export default TopBar;
